/**
 * Shared Modal Component
 * Following Feature-Based Modular Architecture and SOLID principles
 * Reusable modal component with backdrop, animations, and accessibility
 */

import React, { Children, isValidElement, useCallback, useEffect, useId, useRef } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useFocusTrap } from '@/shared/hooks/useFocusTrap'
import { ModalProps } from '@/shared/types/common.types'

import ModalBody from './ModalBody'
import { ModalBusyContext } from './modalContext'
import ModalFooter from './ModalFooter'
import {
  registerModalOnCloseHandler,
  isAnyModalOpen,
  requestModalDismiss,
  type ModalDismissPolicy,
} from './modalStackManager'

type InitialFocus = NonNullable<ModalProps['initialFocus']>

/** First enabled, visible form control of the panel (jsdom has no layout, so no offsetParent test). */
function findFirstField(panel: HTMLElement): HTMLElement | null {
  const fields = panel.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input, select, textarea',
  )
  for (const field of fields) {
    if (field.disabled) continue
    if (field instanceof HTMLInputElement && field.type === 'hidden') continue
    if (field.closest('[hidden], [aria-hidden="true"]')) continue
    return field
  }
  return null
}

function resolveInitialFocusTarget(panel: HTMLElement, initialFocus: InitialFocus): HTMLElement {
  if (typeof initialFocus === 'object') {
    // A ref that is not attached yet falls through to the default instead of losing focus.
    if (initialFocus.current) return initialFocus.current
  }
  const closeButton = panel.querySelector<HTMLElement>('[data-modal-close]')
  if (initialFocus === 'close' && closeButton) return closeButton
  if (initialFocus === 'primary') {
    const primary = panel.querySelector<HTMLElement>('[data-modal-primary]')
    if (primary) return primary
  }
  return findFirstField(panel) ?? closeButton ?? panel
}

const Modal = React.memo<ModalProps>(
  ({
    isOpen,
    onClose,
    title,
    size = 'md',
    closeOnBackdrop = true,
    children,
    className = '',
    testId,
    closeButtonTestId,
    panelTestId,
    titleId,
    describedById,
    description,
    busy = false,
    dismissible = true,
    onDismissAttempt,
    initialFocus = 'first-field',
    role = 'dialog',
  }) => {
    const { t } = useTranslation('shared')
    const modalRef = useRef<HTMLDivElement>(null)

    // Trap focus within modal when open; restores previous focus on close
    useFocusTrap(modalRef, isOpen)

    // The stack manager reads the dismissal policy lazily (at key-press time) from this ref, so a
    // `busy` flip never re-registers the modal and never moves it in the stack.
    const policyRef = useRef({ onClose, busy, dismissible, onDismissAttempt, testId })
    useEffect(() => {
      policyRef.current = { onClose, busy, dismissible, onDismissAttempt, testId }
    })

    // One «dismiss blocked» warning per busy attempt (see ModalDismissPolicy.busyWarned).
    const busyWarnedRef = useRef(false)
    useEffect(() => {
      if (!busy) busyWarnedRef.current = false
    }, [busy])

    const initialFocusRef = useRef(initialFocus)
    useEffect(() => {
      initialFocusRef.current = initialFocus
    })

    // Initial focus. Declared AFTER `useFocusTrap` so it runs after the trap's own
    // "focus the first focusable" (the ✕ button) and the requested target wins.
    useEffect(() => {
      if (!isOpen || !modalRef.current) return
      resolveInitialFocusTarget(modalRef.current, initialFocusRef.current).focus()
    }, [isOpen])

    // Escape closes only the topmost open modal. The manager attaches a
    // single `document` keydown listener while at least one modal is open
    // and detaches it once the stack drains — no leaked module-scope listener.
    useEffect(() => {
      if (!isOpen) return

      const unregister = registerModalOnCloseHandler(
        () => policyRef.current.onClose(),
        (): ModalDismissPolicy => ({ ...policyRef.current, busyWarned: busyWarnedRef }),
        () => modalRef.current,
      )
      document.body.style.overflow = 'hidden'

      return () => {
        unregister()
        if (!isAnyModalOpen()) document.body.style.overflow = 'unset'
      }
    }, [isOpen])

    // Handle backdrop click
    const handleBackdropClick = useCallback(
      (event: React.MouseEvent) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
          requestModalDismiss(onClose, {
            busy,
            dismissible,
            onDismissAttempt,
            testId,
            busyWarned: busyWarnedRef,
          })
        }
      },
      [closeOnBackdrop, onClose, busy, dismissible, onDismissAttempt, testId],
    )

    const handleCloseButtonClick = useCallback(() => {
      requestModalDismiss(onClose, {
        busy,
        dismissible,
        onDismissAttempt,
        testId,
        busyWarned: busyWarnedRef,
      })
    }, [onClose, busy, dismissible, onDismissAttempt, testId])

    // Resolve heading id (used by both the <h3> id and dialog aria-labelledby)
    //
    // ⚠️⚠️ El defecto era la CADENA LITERAL `'modal-title'`, compartida por los
    // 183 llamantes que no pasan `titleId` (de 187). Este componente está hecho
    // para APILARSE —`modalStackManager` existe sólo para eso: «Escape closes
    // only the topmost (last-pushed) modal — stacked modals do not
    // cascade-close»—, y con dos abiertos el documento tenía DOS elementos con
    // el mismo `id` y los DOS diálogos apuntaban ahí con `aria-labelledby`.
    // `getElementById` devuelve el primero, así que el diálogo de ARRIBA se
    // anunciaba con el título del de ABAJO.
    //
    // `useId()` es lo que ya usa `ExportModal` para esto mismo. Un `titleId`
    // explícito sigue mandando: los cuatro que lo pasan lo hacen para enlazar
    // desde fuera.
    const generatedTitleId = useId()
    const resolvedTitleId = titleId ?? generatedTitleId
    const generatedDescriptionId = useId()

    if (!isOpen) return null

    // Slots: a direct <Modal.Body>/<Modal.Footer> child switches the panel to a fixed header,
    // scrolling body and fixed footer. Bare children keep the legacy wrapper byte for byte.
    const usesSlots = Children.toArray(children).some(
      (child) => isValidElement(child) && (child.type === ModalBody || child.type === ModalFooter),
    )
    const resolvedDescribedBy = describedById ?? (description ? generatedDescriptionId : undefined)

    // Size variants - mobile responsive with full screen on small devices
    const sizeClasses = {
      sm: 'max-w-md sm:max-w-md',
      md: 'max-w-lg sm:max-w-lg',
      lg: 'max-w-2xl sm:max-w-2xl',
      xl: 'max-w-4xl sm:max-w-4xl',
    }

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" data-testid={testId}>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />

        {/* Modal container - Mobile responsive */}
        <div className="flex min-h-full items-center justify-center p-0 sm:p-4">
          <div
            ref={modalRef}
            className={`
            relative w-full h-full sm:h-auto ${sizeClasses[size]} transform overflow-hidden${usesSlots ? ' flex flex-col max-h-[90dvh]' : ''}
            ${size === 'lg' || size === 'xl' ? 'rounded-none sm:rounded-lg' : 'rounded-none sm:rounded-lg'}
            bg-crypto-card shadow-xl transition-all duration-300
            ${className}
          `}
            role={role}
            aria-modal="true"
            aria-labelledby={title ? resolvedTitleId : undefined}
            aria-describedby={resolvedDescribedBy}
            tabIndex={-1}
            data-testid={panelTestId}
          >
            {/* Header - Mobile responsive */}
            {title && (
              <div
                className={`flex items-center justify-between p-4 sm:p-6 border-b border-crypto-border${usesSlots ? ' shrink-0' : ''}`}
              >
                <h3
                  id={resolvedTitleId}
                  className="text-base sm:text-lg font-semibold text-crypto-text pr-8"
                >
                  {title}
                </h3>
                <button
                  type="button"
                  className="absolute top-4 right-4 sm:static text-crypto-muted hover:text-crypto-text transition-colors p-2 -m-2 touch-target min-h-[44px] min-w-[44px]"
                  onClick={handleCloseButtonClick}
                  data-modal-close
                  aria-label={t('modal.close')}
                  data-testid={closeButtonTestId}
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {description && (
              <p
                id={generatedDescriptionId}
                className="shrink-0 px-4 pt-3 text-sm text-crypto-muted sm:px-6"
              >
                {description}
              </p>
            )}

            {/* Content - Mobile responsive with scroll */}
            <ModalBusyContext.Provider value={busy}>
              {usesSlots ? (
                <div className="flex min-h-0 flex-1 flex-col">{children}</div>
              ) : (
                <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(100vh-8rem)] sm:max-h-none">
                  {children}
                </div>
              )}
            </ModalBusyContext.Provider>
          </div>
        </div>
      </div>
    )
  },
)

Modal.displayName = 'Modal'

export default Object.assign(Modal, { Body: ModalBody, Footer: ModalFooter })
